import type {
  CreateImagCasoUsoInput,
  CreateImagRegraNegocioInput,
  ImagCasoUso,
  ImagRegraNegocio,
  ImagSolucaoTobe,
  UpdateImagCasoUsoInput,
  UpdateImagRegraNegocioInput,
} from '@internal/orquestra-contract';
import { OrquestraImagineClient } from './OrquestraImagineClient';
import type { OrquestraApi } from './OrquestraApi.types';

function casos(projectId: number, solucaoId: number, funcId: number): string {
  return `/projects/${projectId}/imagine/solucoes/${solucaoId}/funcionalidades/${funcId}/casos-uso`;
}

function regras(projectId: number, solucaoId: number, funcId: number): string {
  return `/projects/${projectId}/imagine/solucoes/${solucaoId}/funcionalidades/${funcId}/regras-negocio`;
}

function vinculos(projectId: number, solucaoId: number): string {
  return `/projects/${projectId}/imagine/solucoes/${solucaoId}/tobe-vinculos`;
}

/**
 * Client concreto do Orquestra. Estende `OrquestraImagineClient` com os métodos do
 * Imagine detalhes (Fase 4.3b: casos de uso, regras de negócio e vínculo solução↔
 * TO-BE) e implementa o `OrquestraApi` completo. É a implementação registrada no
 * `apiFactory`.
 */
export class OrquestraImagineDetailClient
  extends OrquestraImagineClient
  implements OrquestraApi
{
  async listCasosUso(
    projectId: number,
    solucaoId: number,
    funcId: number,
  ): Promise<ImagCasoUso[]> {
    return this.request<ImagCasoUso[]>(casos(projectId, solucaoId, funcId));
  }

  async createCasoUso(
    projectId: number,
    solucaoId: number,
    funcId: number,
    input: CreateImagCasoUsoInput,
  ): Promise<ImagCasoUso> {
    return this.request<ImagCasoUso>(
      casos(projectId, solucaoId, funcId),
      this.jsonInit('POST', input),
    );
  }

  async updateCasoUso(
    projectId: number,
    solucaoId: number,
    funcId: number,
    casoId: number,
    input: UpdateImagCasoUsoInput,
  ): Promise<ImagCasoUso> {
    return this.request<ImagCasoUso>(
      `${casos(projectId, solucaoId, funcId)}/${casoId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteCasoUso(
    projectId: number,
    solucaoId: number,
    funcId: number,
    casoId: number,
  ): Promise<void> {
    await this.request(
      `${casos(projectId, solucaoId, funcId)}/${casoId}`,
      this.jsonInit('DELETE'),
    );
  }

  async listRegrasNegocio(
    projectId: number,
    solucaoId: number,
    funcId: number,
  ): Promise<ImagRegraNegocio[]> {
    return this.request<ImagRegraNegocio[]>(regras(projectId, solucaoId, funcId));
  }

  async createRegraNegocio(
    projectId: number,
    solucaoId: number,
    funcId: number,
    input: CreateImagRegraNegocioInput,
  ): Promise<ImagRegraNegocio> {
    return this.request<ImagRegraNegocio>(
      regras(projectId, solucaoId, funcId),
      this.jsonInit('POST', input),
    );
  }

  async updateRegraNegocio(
    projectId: number,
    solucaoId: number,
    funcId: number,
    regraId: number,
    input: UpdateImagRegraNegocioInput,
  ): Promise<ImagRegraNegocio> {
    return this.request<ImagRegraNegocio>(
      `${regras(projectId, solucaoId, funcId)}/${regraId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteRegraNegocio(
    projectId: number,
    solucaoId: number,
    funcId: number,
    regraId: number,
  ): Promise<void> {
    await this.request(
      `${regras(projectId, solucaoId, funcId)}/${regraId}`,
      this.jsonInit('DELETE'),
    );
  }

  async listSolucaoTobe(
    projectId: number,
    solucaoId: number,
  ): Promise<ImagSolucaoTobe[]> {
    return this.request<ImagSolucaoTobe[]>(vinculos(projectId, solucaoId));
  }

  async linkSolucaoTobe(
    projectId: number,
    solucaoId: number,
    tobeProcessId: number,
  ): Promise<ImagSolucaoTobe> {
    return this.request<ImagSolucaoTobe>(
      vinculos(projectId, solucaoId),
      this.jsonInit('POST', { tobeProcessId }),
    );
  }

  async unlinkSolucaoTobe(
    projectId: number,
    solucaoId: number,
    tobeProcessId: number,
  ): Promise<void> {
    await this.request(
      `${vinculos(projectId, solucaoId)}/${tobeProcessId}`,
      this.jsonInit('DELETE'),
    );
  }
}
